import type { MouseEventHandler } from 'react'
import { IconMessageHeart, IconLifebuoy, IconMap, IconListCheck } from '@tabler/icons-react'
import type { PaymentProvider } from '../../../payments/types'
import type { PricingState } from '../model/usePricing'
import { breakUpPlan } from '../model/breakUpPricing'
import BreakUpPlanCard from './BreakUpPlanCard'
import s from './BreakUpPricing.module.css'

const features = [
  { Icon: IconMessageHeart, title: 'Поддержка ИИ 24/7', text: 'Расскажите, что вас тревожит. ИИ-помощник учитывает контекст ваших разговоров и помогает разобраться в чувствах.' },
  { Icon: IconLifebuoy, title: 'SOS-помощь в трудный момент', text: 'Когда накрывают тревога, боль или навязчивые мысли — откройте практики, которые помогут справиться с эмоциями.' },
  { Icon: IconMap, title: 'Ваш путь восстановления', text: 'Узнайте, на каком этапе отношений или расставания вы находитесь, и отслеживайте изменения своего состояния.' },
  { Icon: IconListCheck, title: 'Следующий шаг', text: 'Получайте небольшие задания на день и неделю, чтобы постепенно возвращать спокойствие, уверенность и контроль над своей жизнью.' },
]

interface Props {
  data: NonNullable<PricingState['data']>
  provider: PaymentProvider
  payingId: string | null
  onPay: MouseEventHandler<HTMLButtonElement>
  payError: string
  payNotice: string
}

export default function BreakUpPricing({ data, provider, payingId, onPay, payError, payNotice }: Props) {
  const products = new Map(data.products.map(product => [String(product.planId), product]))
  const plans = [...data.plans].sort((a, b) => (a.order ?? 99) - (b.order ?? 99))
    .map(plan => breakUpPlan(plan, provider, products.get(plan.id)))
  return <div className={s.wrap}>
    <section className={s.intro}>
      <h1>Хьюмантек ведёт по всем этапам расставания с заданиями, одобренными экспертом</h1>
      <p>Маленькие шаги, бережный тон и практика — в любое время дня и ночи.</p>
      <div className={s.pills}>
        <span><IconMap size={14} aria-hidden="true" />Путь восстановления</span>
        <span><IconListCheck size={14} aria-hidden="true" />Задания на день</span>
      </div>
    </section>
    <div className={s.cards}>
      {plans.map(plan => <BreakUpPlanCard key={plan.id} plan={plan} payingId={payingId} onPay={onPay} />)}
    </div>
    {data.catalogError && <p className={s.error} role="alert">{data.catalogError}</p>}
    {payError && <p className={s.error} role="alert">{payError}</p>}
    {payNotice && <p className={s.notice} role="status">{payNotice}</p>}
    <section className={s.included}>
      <h2>Что входит в «Неделю», «Месяц» и «Программу»</h2>
      <p className={s.includedLead}>Персональная поддержка, практики и пошаговый путь к эмоциональному восстановлению.</p>
      <div className={s.features}>
        {features.map(({ Icon, title, text }) => <div key={title} className={s.feature}>
          <Icon size={21} aria-hidden="true" />
          <div><h3>{title}</h3><p>{text}</p></div>
        </div>)}
      </div>
    </section>
  </div>
}
